import React, { FunctionComponent, ChangeEvent, useEffect } from 'react'
import { Checkbox, Heading, Text, useIsDesignLanguage, DesignLanguage, Icon, BaseCell } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'

import { ModuleContainer } from '../ModuleContainer'
import { useCheckoutSizes } from '../../utils/responsive'
import { useCheckoutColor } from '../../utils/color'
import {
  setDonationAmount,
  setDonationStatus,
  setHasUpdatedDonation,
  setIsUpdatingDonation,
  useAppDispatch,
  useAppRouter,
  useCheckoutState,
  useDonation,
  useDonationCampaignDetails,
  useIsBillingAgreementApprovalProcess,
  useIsDonationEnabled,
} from '../../state'
import { CheckoutStatus, DonationStatus } from '../../model'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useMoney } from '../Money/useMoney'
import { selectDonationCampaignContent } from './donationCampaignContent'

export const Donation: FunctionComponent = () => {
  const { locale } = useAppRouter()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { subtextColor } = useCheckoutColor()
  const { status } = useCheckoutState()
  const donationCampaignDetails = useDonationCampaignDetails()
  const isBillingAgreementApprovalFlow = useIsBillingAgreementApprovalProcess()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const isDisabled = status > CheckoutStatus.Started
  const { donationAmount, hasUpdatedDonation } = useDonation()
  const isDonationEnabled = useIsDonationEnabled()

  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const { formatMoney } = useMoney({ locale, currency: donationCampaignDetails?.amount.currency })
  const amount = formatMoney(donationCampaignDetails?.amount.amount)
  const strong = isArcadeDesignLanguage ? <span /> : <strong />
  const donationCampaignContent = selectDonationCampaignContent({
    amount,
    campaignId: donationCampaignDetails?.campaignId,
    strong,
    t,
    termsUrl: t('donation:termsUrl'),
  })

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(setIsUpdatingDonation(true))
    setTimeout(() => {
      const isChecked = e.target.checked
      dispatch(setDonationAmount(isChecked ? Number(donationCampaignDetails?.amount.amount) : 0))
      dispatch(setIsUpdatingDonation(false))
      logEvent(TrackingEvent.TOGGLED_DONATION_BUTTON, { checked: isChecked })
    }, 700)
  }

  useEffect(() => {
    if (!hasUpdatedDonation) {
      dispatch(setHasUpdatedDonation(true))
      if (isDonationEnabled) {
        dispatch(setDonationStatus(DonationStatus.Active))
      } else {
        dispatch(setDonationStatus(DonationStatus.Inactive))
      }
    }
  }, [isDonationEnabled])

  if (!isDonationEnabled || isBillingAgreementApprovalFlow || donationCampaignContent == null) {
    return null
  }

  const renderLabel = () => {
    if (isArcadeDesignLanguage) {
      return (
        <Text size={textSize} bold id='donation-label'>
          {donationCampaignContent.title}
        </Text>
      )
    }

    return <Heading size={textSize}>{donationCampaignContent.title}</Heading>
  }

  const renderContent = () => {
    const bodyTextColor = isArcadeDesignLanguage ? 'Standard' : subtextColor

    return (
      <Text size={smallTextSize} color={bodyTextColor}>
        {donationCampaignContent.body}
      </Text>
    )
  }

  return (
    <>
      {isArcadeDesignLanguage ? (
        <BaseCell
          asset={<Icon name='HsaCard' size='Medium' />}
          label={renderLabel()}
          body={renderContent()}
          breakout
          accessory={
            <Checkbox
              size='Medium'
              defaultChecked={!!donationAmount}
              disabled={isDisabled}
              aria-labelledby='donation-label'
              onChange={handleOnChange}
              testNameSpace='donation'
              reversed
            />
          }
        />
      ) : (
        <ModuleContainer padding={{ vertical: 'Large', horizontal: 'XLarge' }} showBorder>
          <Checkbox
            size='Small'
            defaultChecked={!!donationAmount}
            disabled={isDisabled}
            label={
              <>
                {renderLabel()}
                {renderContent()}
              </>
            }
            onChange={handleOnChange}
            testNameSpace='donation'
          />
        </ModuleContainer>
      )}
      <TrackOnMount eventName={TrackingEvent.VIEWED_DONATION} eventProperties={{ campaign: donationCampaignDetails?.campaignId }} />
    </>
  )
}
